// # Exam Day Stats Component for Step 1
import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaTasks, FaExclamationTriangle, FaBookReader, FaSync, FaClock } from 'react-icons/fa';
import { Edit, Check, Close } from '@mui/icons-material';  // # Add icons
import PropTypes from 'prop-types';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { differenceInDays } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import './stats1_examday.css';

// # Import chapter topics and enabled chapters list
import { chapterTopics } from '../../../general/library/step1/step1_chapter.js';
import { chapters } from '../../../general/library/step1/library_step_1.js';

// # Chart colors
const CHART_COLORS = {
  completed: '#4CAF50',
  remaining: '#FF9800',
  critical: '#f44336',
  reviewed: '#2196F3',
  toReview: '#9C27B0'
};

// # Define target score tiers
const TARGET_SCORE_TIERS = Array.from({ length: 8 }, (_, i) => ({
  value: 200 + (i * 10),
  label: `${200 + (i * 10)}+`
}));

function ExamDayStats({ isAdmin, isTutor, isMentorView, isDarkMode, targetUserId }) {
  const { user } = useAuthStore();
  const [examDate, setExamDate] = useState(null);
  const [remainingDays, setRemainingDays] = useState(null);
  const [targetScore, setTargetScore] = useState(null);
  const [isEditingScore, setIsEditingScore] = useState(false);  // # Add edit state
  const [taskStats, setTaskStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    remainingTasks: 0,
    reviewedTasks: 0,    // # Add new state for reviewed topics
    toReviewTasks: 0,    // # Add new state for topics to review
    chapterProgress: []
  });
  // # Add new state for weak points and qbank stats
  const [weakPointsCount, setWeakPointsCount] = useState(0);
  const [qbankStats, setQbankStats] = useState({
    nbme: { total: 11, completed: 0 },  // 11 NBMEs total
    uworld: { total: 27, completed: 0 }, // 27 UWorld chapters
    amboss: { total: 27, completed: 0 }  // 27 Amboss chapters
  });
  const [weakPointsData, setWeakPointsData] = useState({
    hardTopics: 0,
    veryHardTopics: 0,
    weakSpots: 0,
    qbankWeakPoints: {
      nbme: 0,
      uworld: 0,
      amboss: 0
    }
  });

  // # Get enabled chapters
  const enabledChapters = chapters
    .filter(chapter => chapter.enabled)
    .map(chapter => {
      // # Handle special cases for chapter slugs
      if (chapter.title === 'Hematology & Oncology') {
        return 'hematology_and_oncology';
      } else if (chapter.title === 'Musculoskeletal & Connective Tissue') {
        return 'musculoskeletal_and_connective_tissue';
      }
      return chapter.title.toLowerCase().replace(/[\s&]+/g, '_');
    });

  // # Load exam date and target score from Firestore
  useEffect(() => {
    // # Get the correct user ID based on roles
    const userId = targetUserId || user?.uid;
    if (!userId) return;

    const menteeRef = doc(db, 's1_mentee', userId);
    const unsubscribe = onSnapshot(menteeRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.exam_day) {
          setExamDate(new Date(data.exam_day));
          calculateRemainingDays(new Date(data.exam_day));
        }
        if (data.target_score) {
          setTargetScore(data.target_score);
        }
      }
    });

    return () => unsubscribe();
  }, [user, targetUserId]);

  // # Load task progress - Updated to filter enabled chapters
  useEffect(() => {
    // # Get the correct user ID based on roles
    const userId = targetUserId || user?.uid;
    if (!userId) return;

    const progressRef = doc(db, 'my_lib_progress', userId);
    const unsubscribe = onSnapshot(progressRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.step1) {
          let totalTasks = 0;
          let completedTasks = 0;
          let reviewedTasks = 0;  // # Count for topics reviewed at least once
          let toReviewTasks = 0;  // # Count for completed topics not yet reviewed
          let chapterProgress = [];

          // # Process only enabled chapters
          enabledChapters.forEach(chapterSlug => {
            const topics = chapterTopics[chapterSlug] || [];
            const chapterData = data.step1[chapterSlug] || {};
            const chapterTotalTasks = topics.length;
            
            // # Get all topic keys excluding metadata fields
            const topicKeys = Object.keys(chapterData)
              .filter(key => key !== 'iscomplete' && key !== 'iscomplete_at');
            
            // # Count completed topics
            const chapterCompletedTasks = topicKeys
              .filter(key => chapterData[key]?.completed)
              .length;
            
            // # Count topics with at least one review
            const chapterReviewedTasks = topicKeys
              .filter(key => 
                chapterData[key]?.completed && 
                chapterData[key]?.reviewTimestamps && 
                chapterData[key].reviewTimestamps.length > 0
              )
              .length;
            
            // # Count completed topics without reviews
            const chapterToReviewTasks = topicKeys
              .filter(key => 
                chapterData[key]?.completed && 
                (!chapterData[key]?.reviewTimestamps || 
                 chapterData[key].reviewTimestamps.length === 0)
              )
              .length;

            totalTasks += chapterTotalTasks;
            completedTasks += chapterCompletedTasks;
            reviewedTasks += chapterReviewedTasks;
            toReviewTasks += chapterToReviewTasks;

            // # Add to chapter progress
            chapterProgress.push({
              name: chapterSlug.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
              total: chapterTotalTasks,
              completed: chapterCompletedTasks,
              remaining: chapterTotalTasks - chapterCompletedTasks
            });
          });

          setTaskStats({
            totalTasks,
            completedTasks,
            remainingTasks: totalTasks - completedTasks,
            reviewedTasks,    // # Add new stat
            toReviewTasks,    // # Add new stat
            chapterProgress
          });
        }
      }
    });

    return () => unsubscribe();
  }, [user, enabledChapters]);

  // # Fetch weak points data
  useEffect(() => {
    // # Get the correct user ID based on roles
    const userId = targetUserId || user?.uid;
    if (!userId) return;

    // # Get library progress for hard/very hard topics
    const libraryProgressRef = doc(db, 'my_lib_progress', userId);
    const menteeRef = doc(db, 's1_mentee', userId);

    const unsubscribeLibrary = onSnapshot(libraryProgressRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data?.step1) {
          let hardCount = 0;
          let veryHardCount = 0;

          Object.values(data.step1).forEach(chapterData => {
            if (typeof chapterData === 'object') {
              Object.entries(chapterData).forEach(([key, topicData]) => {
                if (key !== 'iscomplete' && 
                    key !== 'iscomplete_at' && 
                    key !== 'IsChapStarted' && 
                    key !== 'ChptStartedAt' && 
                    topicData?.completed) {
                  if (topicData.difficulty === 'hard') {
                    hardCount++;
                  } else if (topicData.difficulty === 'oh_no') {
                    veryHardCount++;
                  }
                }
              });
            }
          });

          setWeakPointsData(prev => ({
            ...prev,
            hardTopics: hardCount,
            veryHardTopics: veryHardCount
          }));
        }
      }
    });

    // # Get weak spots and qbank data
    const unsubscribeMentee = onSnapshot(menteeRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        
        // # Count weak spots
        const weakSpotsCount = Object.keys(data.weakSpots || {}).length;

        // # Count qbank weak points
        const qbankWeakPoints = {
          nbme: Object.values(data.nbme || {}).reduce((sum, exam) => 
            sum + (exam?.weaknesses?.length || 0), 0),
          uworld: Object.values(data.uworld || {}).reduce((sum, chapter) => 
            sum + (chapter?.weaknesses?.length || 0), 0),
          amboss: Object.values(data.amboss || {}).reduce((sum, chapter) => 
            sum + (chapter?.weaknesses?.length || 0), 0)
        };

        setWeakPointsData(prev => ({
          ...prev,
          weakSpots: weakSpotsCount,
          qbankWeakPoints
        }));
      }
    });

    return () => {
      unsubscribeLibrary();
      unsubscribeMentee();
    };
  }, [user]);

  // # Calculate remaining days
  const calculateRemainingDays = (date) => {
    if (!date) return;
    const today = new Date();
    const days = differenceInDays(date, today);
    setRemainingDays(days);
  };

  // # Handle date change
  const handleDateChange = async (newDate) => {
    // # Allow editing if it's the user's own data or if admin/tutor
    if (!user?.uid || !newDate) return;
    
    // # If viewing someone else's data, only admin/tutor can edit
    if (targetUserId && targetUserId !== user.uid && !isAdmin && !isTutor) return;

    const userId = targetUserId || user.uid;
    setExamDate(newDate);
    calculateRemainingDays(newDate);

    try {
      const menteeRef = doc(db, 's1_mentee', userId);
      await setDoc(menteeRef, {
        exam_day: newDate.toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving exam date:', error);
    }
  };

  // # Handle target score change
  const handleTargetScoreChange = async (event) => {
    // # Allow editing if it's the user's own data or if admin/tutor
    const score = parseInt(event.target.value);
    if (isNaN(score)) return;
    
    // # If viewing someone else's data, only admin/tutor can edit
    if (targetUserId && targetUserId !== user.uid && !isAdmin && !isTutor) return;
    
    setTargetScore(score);
  };

  // # Save target score
  const saveTargetScore = async () => {
    // # Allow editing if it's the user's own data or if admin/tutor
    if (!user?.uid || !targetScore) return;
    
    // # If viewing someone else's data, only admin/tutor can edit
    if (targetUserId && targetUserId !== user.uid && !isAdmin && !isTutor) return;

    const userId = targetUserId || user.uid;
    try {
      const menteeRef = doc(db, 's1_mentee', userId);
      await setDoc(menteeRef, {
        target_score: targetScore
      }, { merge: true });
      setIsEditingScore(false);
    } catch (error) {
      console.error('Error saving target score:', error);
    }
  };

  // # Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="xstats1_exam_tooltip">
          <p className="xstats1_exam_tooltip_label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="xstats1_exam_tooltip_value" style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`xstats1_exam_container ${isDarkMode ? 'dark' : ''}`}>
      <div className="xstats1_exam_header">
        <div className="xstats1_exam_icon">
          <FaCalendarAlt />
        </div>
        <h2>Exam Day Preparation</h2>
      </div>

      <div className="xstats1_exam_date_section">
        <div className="xstats1_exam_date_grid">
          {remainingDays !== null && (
            <div className={`xstats1_exam_countdown ${remainingDays < 30 ? 'urgent' : ''}`}>
              <h3>{remainingDays} Days Remaining</h3>
              <p>{remainingDays < 15 ? 'It is happening soon!' : 'Keep up the good work!'}</p>
            </div>
          )}

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Exam Date"
              value={examDate}
              onChange={handleDateChange}
              className="xstats1_exam_date_picker"
              disablePast
              disabled={targetUserId && targetUserId !== user?.uid && !isAdmin && !isTutor}
            />
          </LocalizationProvider>

          <div className="xstats1_exam_target_score">
            {(!targetScore || isEditingScore) ? (
              <div className="xstats1_exam_target_edit">
                <select
                  value={targetScore || ''}
                  onChange={handleTargetScoreChange}
                  className="xstats1_exam_target_select"
                  disabled={targetUserId && targetUserId !== user?.uid && !isAdmin && !isTutor}
                >
                  <option value="">Select Target Score</option>
                  {TARGET_SCORE_TIERS.map(tier => (
                    <option key={tier.value} value={tier.value}>
                      {tier.label}
                    </option>
                  ))}
                </select>
                <div className="xstats1_exam_target_actions">
                  <button 
                    className="xstats1_exam_target_btn save" 
                    onClick={saveTargetScore}
                    title="Save"
                    disabled={targetUserId && targetUserId !== user?.uid && !isAdmin && !isTutor}
                  >
                    <Check />
                  </button>
                  {targetScore && (
                    <button 
                      className="xstats1_exam_target_btn cancel" 
                      onClick={() => setIsEditingScore(false)}
                      title="Cancel"
                      disabled={targetUserId && targetUserId !== user?.uid && !isAdmin && !isTutor}
                    >
                      <Close />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="xstats1_exam_target_display">
                <span>Target Score: {targetScore}+</span>
                {/* Show edit button if it's user's own data or if admin/tutor */}
                {(!targetUserId || targetUserId === user?.uid || isAdmin || isTutor) && (
                  <button 
                    className="xstats1_exam_target_btn edit"
                    onClick={() => setIsEditingScore(true)}
                    title="Edit"
                  >
                    <Edit />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="xstats1_exam_stats_grid">
        <div className="xstats1_exam_stat_card">
          <div className="xstats1_exam_stat_icon">
            <FaTasks />
          </div>
          <div className="xstats1_exam_stat_content">
            <h4>Total Topics</h4>
            <div className="xstats1_exam_stat_value">{taskStats.totalTasks}</div>
          </div>
        </div>

        <div className="xstats1_exam_stat_card">
          <div className="xstats1_exam_stat_icon completed">
            <FaTasks />
          </div>
          <div className="xstats1_exam_stat_content">
            <h4>Completed</h4>
            <div className="xstats1_exam_stat_value">{taskStats.completedTasks}</div>
          </div>
        </div>

        <div className="xstats1_exam_stat_card">
          <div className="xstats1_exam_stat_icon remaining">
            <FaExclamationTriangle />
          </div>
          <div className="xstats1_exam_stat_content">
            <h4>Remaining</h4>
            <div className="xstats1_exam_stat_value">{taskStats.remainingTasks}</div>
          </div>
        </div>
        
        <div className="xstats1_exam_stat_card">
          <div className="xstats1_exam_stat_icon reviewed">
            <FaSync />
          </div>
          <div className="xstats1_exam_stat_content">
            <h4>Topics Reviewed</h4>
            <div className="xstats1_exam_stat_value">{taskStats.reviewedTasks}</div>
          </div>
        </div>
        
        <div className="xstats1_exam_stat_card">
          <div className="xstats1_exam_stat_icon to-review">
            <FaClock />
          </div>
          <div className="xstats1_exam_stat_content">
            <h4>Topics to Review</h4>
            <div className="xstats1_exam_stat_value">{taskStats.toReviewTasks}</div>
          </div>
        </div>
      </div>

      {/* Topics Per Day Section */}
      {remainingDays > 0 && taskStats.remainingTasks > 0 && (
        <div className="xstats1_exam_topics_per_day">
          <div className="xstats1_exam_topics_per_day_icon">
            <FaBookReader />
          </div>
          <div className="xstats1_exam_topics_per_day_content">
            <h4>Daily Goal</h4>
            <div className="xstats1_exam_topics_per_day_value">
              {Math.ceil(taskStats.remainingTasks / remainingDays)} topics/day
            </div>
            <p className="xstats1_exam_topics_per_day_text">
              To complete all remaining topics before exam day
            </p>
          </div>
        </div>
      )}

      <div className="xstats1_exam_charts_section">
        <div className="xstats1_exam_chart_card">
          <h3>Overall Progress</h3>
          <div className="xstats1_exam_chart_container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Reviewed', value: taskStats.reviewedTasks },
                    { name: 'To Review', value: taskStats.toReviewTasks },
                    { name: 'Remaining', value: taskStats.remainingTasks }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  <Cell fill={CHART_COLORS.reviewed} />
                  <Cell fill={CHART_COLORS.toReview} />
                  <Cell fill={CHART_COLORS.remaining} />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="xstats1_exam_chart_card">
          <h3>Progress by Chapter</h3>
          <div className="xstats1_exam_chart_container">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={taskStats.chapterProgress}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="completed" stackId="a" fill={CHART_COLORS.completed} name="Completed" />
                <Bar dataKey="remaining" stackId="a" fill={CHART_COLORS.remaining} name="Remaining" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="xstats1_exam_bottom_message">
        <p>
          You currently have {remainingDays} days until your exam. There are {taskStats.remainingTasks} topics left to study and {taskStats.toReviewTasks} topics that need review. Your challenging areas include {weakPointsData.hardTopics} hard topics, {weakPointsData.veryHardTopics} very hard topics, {weakPointsData.weakSpots} weak spots, and {weakPointsData.qbankWeakPoints.nbme + weakPointsData.qbankWeakPoints.uworld + weakPointsData.qbankWeakPoints.amboss} points marked as weak in your QBanks. Keep pushing forward and maintain focus on these areas while completing your remaining QBank sections.
        </p>
      </div>
    </div>
  );
}

ExamDayStats.propTypes = {
  isDarkMode: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isTutor: PropTypes.bool,
  isMentorView: PropTypes.bool,
  targetUserId: PropTypes.string
};

export default ExamDayStats;
